import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { RequestTypes } from './requestTypes'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { s3Upload } from '@core/react-query/helpers'

export const companyQueryKeys = createQueryKeys('company', {
  getCompanies: ({
    organisationId,
    page,
    size,
    term,
  }: RequestTypes['getCompanies']) => {
    return {
      queryKey: [{ organisationId, page, size, term }],
      queryFn: async () => {
        const queryParams = createQueryKeyString({
          page,
          size,
          term,
        })

        const { data } = await APIAxiosInstance.get(
          `organisations/${organisationId}/companies${queryParams}`,
        )

        return data
      },
    }
  },
  getCompanyDetails: ({
    organisationId,
    companyId,
  }: RequestTypes['getCompanyDetails']) => {
    return {
      queryKey: [{ organisationId, companyId }],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `organisations/${organisationId}/companies/${companyId}`,
        )
        return data
      },
    }
  },
  getAssignableProjects: ({
    organisationId,
    companyId,
    targetOrganisationRole,
    page,
    size,
    term,
  }: RequestTypes['getAssignableProjects']) => {
    return {
      queryKey: [
        { organisationId, companyId, targetOrganisationRole, page, size, term },
      ],
      queryFn: async ({ pageParam = 1 }) => {
        const queryParams = createQueryKeyString({
          targetOrganisationRole,
          page: page ? page : pageParam,
          size,
          term,
        })

        const { data } = await APIAxiosInstance.get(
          `organisations/${organisationId}/companies/${companyId}/users/assignable-projects${queryParams}`,
        )

        return data
      },
    }
  },
})

const companyMutationKeys = createMutationKeys('company', {
  mergeCompaniesTemplate: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
    }: RequestTypes['mergeCompaniesTemplate']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/merge/download`,
      )
      return data
    },
  },
  mergeCompanies: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      mergedIntoCompanyId,
    }: RequestTypes['mergeCompanies']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/merge`,
        {
          companyId,
          mergedIntoCompanyId,
        },
      )
      return data
    },
  },
  bulkMergeCompanies: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      file,
      folderName,
    }: RequestTypes['bulkMergeCompanies']) => {
      const companiesDocumentKey = await s3Upload({
        file,
        folderName,
      })

      await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/merge/bulk`,
        {
          companiesDocumentKey,
        },
      )
    },
  },
  addCompany: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      name,
      code,
      properties,
      optionalComplianceSettingIds,
    }: RequestTypes['addCompany']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies`,
        {
          name,
          code,
          properties,
          optionalComplianceSettingIds,
        },
      )
      return data
    },
  },
  editCompany: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      name,
      code,
      properties,
    }: RequestTypes['editCompany']) => {
      const { data } = await APIAxiosInstance.put(
        `organisations/${organisationId}/companies/${companyId}`,
        {
          name,
          code,
          properties,
        },
      )
      return data
    },
  },
  deleteCompany: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
    }: RequestTypes['deleteCompany']) => {
      await APIAxiosInstance.delete(
        `organisations/${organisationId}/companies/${companyId}`,
      )
    },
  },
  uploadCompaniesTemplate: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
    }: RequestTypes['uploadCompaniesTemplate']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/upload/template`,
      )
      return data
    },
  },
  bulkUploadCompanies: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      file,
      folderName,
    }: RequestTypes['bulkUploadCompanies']) => {
      const companiesDocumentKey = await s3Upload({
        file,
        folderName,
      })

      await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/upload`,
        {
          companiesDocumentKey,
        },
      )
    },
  },
  bulkDownloadCompanies: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
    }: RequestTypes['bulkDownloadCompanies']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/download`,
      )
      return data
    },
  },
  approveCompany: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      projectIds,
      optionalComplianceSettingIds,
    }: RequestTypes['approveCompany']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/approve`,
        { projectIds, optionalComplianceSettingIds },
      )
      return data
    },
  },
  assignCompliancesToCompany: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      complianceSettingIds,
    }: RequestTypes['assignCompliancesToCompany']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/compliances`,
        { complianceSettingIds },
      )
      return data
    },
  },
  rejectCompany: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      reason,
    }: RequestTypes['rejectCompany']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/reject`,
        { reason },
      )
      return data
    },
  },
  requestChanges: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      comment,
    }: RequestTypes['requestChanges']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/request-changes`,
        { comment },
      )
      return data
    },
  },
  reopenForApproval: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
    }: RequestTypes['reopenForApproval']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/reopen-for-approval`,
      )
      return data
    },
  },
  inviteCompanyUser: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      ...payload
    }: RequestTypes['inviteCompanyUser']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/users/invite`,
        payload,
      )
      return data
    },
  },
  removeCompanyUser: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      userId,
    }: RequestTypes['removeCompanyUser']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/users/${userId}/remove`,
      )
      return data
    },
  },
})

export const companyKeys = mergeQueryKeys(
  companyQueryKeys,
  companyMutationKeys,
).company
